import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useAtlas } from "@/context/AtlasContext";
import { PageHeader } from "@/components/shared/PageHeader";
import { StatusPill } from "@/components/shared/StatusPill";
import { FormModal } from "@/components/shared/FormModal";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { BookOpen, Plus, Check, X, ShieldCheck, History, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

const AUTH_TONE = { binding_policy: "danger", approval_required: "warning", approved_procedure: "success", guidance: "info", validated_pattern: "brand", candidate: "neutral", inference: "neutral" };

export default function Knowledge() {
  const { has } = useAtlas();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [quick, setQuick] = useState("");
  const [qtype, setQtype] = useState("policy");
  const canManage = has("knowledge.manage");
  const canApprove = has("knowledge.approve");

  const load = () => api.get("/knowledge").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const saveQuick = async () => {
    const body = quick.trim(); if (!body) return;
    const title = body.split("\n")[0].slice(0, 80);
    try { await api.post("/knowledge", { title, body, type: qtype, authority: "guidance", enforcement: "recommend", status: "candidate", scope_apps: [] }); toast.success("Added — Atlas will use this once it's approved"); setQuick(""); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const create = async (v) => { await api.post("/knowledge", { ...v, scope_apps: v.scope_apps || [] }); toast.success("Knowledge created"); load(); };
  const openDetail = async (it) => { const { data } = await api.get(`/knowledge/${it.id}`); setDetail(data); };
  const transition = async (id, status) => {
    try { await api.post(`/knowledge/${id}/transition`, { status }); toast.success(`Marked ${status}`); setDetail(null); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const filtered = filter === "all" ? items : items.filter((i) => i.status === filter);
  const counts = items.reduce((a, i) => { a[i.status] = (a[i.status] || 0) + 1; return a; }, {});

  return (
    <div>
      <PageHeader title="Atlas Knowledge" subtitle="How this company operates — governed, versioned, with provenance">
        {canManage && <Button onClick={() => setOpen(true)} variant="outline" data-testid="knowledge-create-button"><Plus className="w-4 h-4 mr-1" /> Detailed entry</Button>}
      </PageHeader>

      {canManage && (
        <div className="rounded-2xl border p-4 sm:p-5 mb-6 bg-gradient-to-br from-indigo-50 via-white to-white" style={{ borderColor: "#e5e7eb" }} data-testid="knowledge-quick-capture">
          <div className="flex items-center gap-2 mb-2"><span className="w-7 h-7 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center"><Sparkles className="w-4 h-4 text-white" /></span><span className="font-heading font-semibold text-slate-900">Teach Atlas how you operate</span></div>
          <p className="text-xs text-slate-500 mb-2.5">Write a rule, preference or lesson in plain language. Atlas will follow it across every app once it's approved.</p>
          <textarea value={quick} onChange={(e) => setQuick(e.target.value)} rows={3} data-testid="knowledge-quick-input"
            placeholder="e.g. We only use Vendor B when Vendor A can't fulfill. Deposits are 50% upfront on any job over $5,000."
            className="w-full rounded-xl border px-3 py-2.5 text-sm bg-white resize-none focus:outline-none focus:ring-2 focus:ring-indigo-200" style={{ borderColor: "#e5e7eb" }} />
          <div className="flex flex-wrap items-center gap-1.5 mt-2.5">
            <span className="text-xs text-slate-400 mr-0.5">This is a</span>
            {["policy", "procedure", "playbook", "preference", "lesson"].map((t) => (
              <button key={t} onClick={() => setQtype(t)} data-testid={`knowledge-quick-type-${t}`}
                className={cn("px-2.5 py-1 rounded-full text-xs border capitalize transition-colors", qtype === t ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400")}>{t}</button>
            ))}
            <Button size="sm" className="ml-auto" disabled={!quick.trim()} onClick={saveQuick} data-testid="knowledge-quick-save"><Plus className="w-4 h-4 mr-1" /> Add to knowledge</Button>
          </div>
        </div>
      )}

      <div className="flex gap-2 mb-4 flex-wrap">
        {["all", "candidate", "approved", "deprecated"].map((s) => (
          <button key={s} onClick={() => setFilter(s)} data-testid={`knowledge-filter-${s}`}
            className={`px-3 py-1 rounded-full text-xs border capitalize ${filter === s ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200"}`}>
            {s}{s !== "all" && counts[s] ? ` (${counts[s]})` : ""}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="knowledge-grid">
        {filtered.map((k) => (
          <button key={k.id} onClick={() => openDetail(k)} data-testid={`knowledge-card-${k.id}`} className="text-left bg-white border border-slate-200 rounded-xl p-4 hover:border-blue-300 hover:shadow-md transition-all">
            <div className="flex items-center justify-between gap-2">
              <span className="text-[10px] uppercase tracking-wide text-slate-400">{k.type?.replace(/_/g, " ")}</span>
              <StatusPill value={k.status} />
            </div>
            <div className="font-heading font-semibold text-slate-900 mt-1">{k.title}</div>
            <p className="text-sm text-slate-500 mt-1 line-clamp-2">{k.body}</p>
            <div className="flex items-center gap-2 mt-2">
              <StatusPill value={k.authority?.replace(/_/g, " ")} tone={AUTH_TONE[k.authority] || "neutral"} />
              {k.source === "ai" && <span className="text-[10px] text-indigo-500 uppercase">AI proposed</span>}
            </div>
          </button>
        ))}
        {filtered.length === 0 && <p className="text-sm text-slate-400">No knowledge items.</p>}
      </div>

      <FormModal open={open} onOpenChange={setOpen} title="New Knowledge" testid="knowledge-form-modal" onSubmit={create}
        fields={[
          { name: "title", label: "Title", required: true },
          { name: "body", label: "Body", type: "textarea", required: true },
          { name: "type", label: "Type", type: "select", default: "playbook", options: ["policy", "procedure", "playbook", "fact", "preference", "lesson", "pattern", "candidate_insight"].map((v) => ({ value: v, label: v.replace(/_/g, " ") })) },
          { name: "authority", label: "Authority", type: "select", default: "guidance", options: ["binding_policy", "approval_required", "approved_procedure", "guidance", "validated_pattern", "candidate", "inference"].map((v) => ({ value: v, label: v.replace(/_/g, " ") })) },
          { name: "enforcement", label: "Enforcement", type: "select", default: "recommend", options: ["block", "require_approval", "warn", "recommend", "inform"].map((v) => ({ value: v, label: v })) },
          { name: "status", label: "Status", type: "select", default: "candidate", options: ["candidate", "approved"].map((v) => ({ value: v, label: v })) },
        ]} />

      <Sheet open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          {detail && (
            <>
              <SheetHeader><SheetTitle className="font-heading">{detail.title}</SheetTitle></SheetHeader>
              <div className="mt-3 space-y-4">
                <div className="flex flex-wrap gap-2">
                  <StatusPill value={detail.status} />
                  <StatusPill value={detail.authority?.replace(/_/g, " ")} tone={AUTH_TONE[detail.authority]} />
                  <span className="text-xs bg-slate-100 px-2 py-0.5 rounded-full border border-slate-200">{detail.type?.replace(/_/g, " ")}</span>
                  <span className="text-xs bg-slate-100 px-2 py-0.5 rounded-full border border-slate-200">enforce: {detail.enforcement}</span>
                </div>
                <p className="text-sm text-slate-700 whitespace-pre-wrap">{detail.body}</p>
                <div className="text-xs text-slate-500 space-y-1 bg-slate-50 rounded-lg p-3 border border-slate-100">
                  <div className="font-semibold text-slate-600 flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5" /> Provenance</div>
                  <div>Source: {detail.source} · v{detail.version} · confidence {detail.confidence ?? "—"}</div>
                  <div>Created by: {detail.created_by_name || "—"}</div>
                  <div>Approved: {detail.approved_at ? new Date(detail.approved_at).toLocaleString() : "not approved"}</div>
                </div>
                {detail.versions?.length > 0 && (
                  <div>
                    <div className="text-xs font-semibold text-slate-600 flex items-center gap-1 mb-1"><History className="w-3.5 h-3.5" /> Version history</div>
                    {detail.versions.map((v) => <div key={v.id} className="text-xs text-slate-500 border-b border-slate-50 py-1">v{v.version} · {v.status} · {v.note} · {new Date(v.created_at).toLocaleDateString()}</div>)}
                  </div>
                )}
                {canApprove && detail.status === "candidate" && (
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => transition(detail.id, "approved")} data-testid="knowledge-approve"><Check className="w-4 h-4 mr-1" /> Approve</Button>
                    <Button size="sm" variant="outline" className="border-red-200 text-red-600" onClick={() => transition(detail.id, "rejected")} data-testid="knowledge-reject"><X className="w-4 h-4 mr-1" /> Reject</Button>
                  </div>
                )}
                {canApprove && detail.status === "approved" && (
                  <Button size="sm" variant="outline" onClick={() => transition(detail.id, "deprecated")} data-testid="knowledge-deprecate">Deprecate</Button>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
